import React, { useState } from 'react';
import { useChatStore } from '../../store/chatStore';

/**
 * ChatInput - Text input with send button for the AI chat panel
 *
 * Requirements:
 * - 1.3: User can type and send messages
 * - 8.3: Messages sent via Enter key or send button
 * - 8.5: Input disabled during streaming, re-enabled on completion
 */
export const ChatInput: React.FC = () => {
  const [value, setValue] = useState('');
  const { isStreaming, sendMessage } = useChatStore();

  const isDisabled = isStreaming;
  const isSendDisabled = isDisabled || value.trim().length === 0;

  const handleSubmit = () => {
    const trimmed = value.trim();
    if (!trimmed || isStreaming) return;
    sendMessage(trimmed);
    setValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="flex items-center gap-2">
      <input
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={isDisabled}
        aria-label="Type your message"
        aria-disabled={isDisabled}
        placeholder="Ask about your cloud costs..."
        className={`flex-1 rounded-lg border border-border px-3 py-2 text-sm text-foreground placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring dark:border-border dark:bg-secondary dark:text-white dark:placeholder-gray-400 dark:focus:ring-ring dark:focus:border-ring ${
          isDisabled ? 'opacity-50 cursor-not-allowed' : ''
        }`}
      />
      <button
        onClick={handleSubmit}
        disabled={isSendDisabled}
        aria-label="Send message"
        aria-disabled={isSendDisabled}
        className={`inline-flex items-center justify-center rounded-lg p-2 text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring dark:bg-primary dark:hover:bg-primary ${
          isSendDisabled ? 'opacity-50 cursor-not-allowed' : ''
        }`}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
        </svg>
      </button>
    </div>
  );
};
